/**
 * use-language-choice.tsx — the player's language, as a control offers it:
 * the languages the client ships (LANGUAGES, i18n/config.ts), each named in
 * its own words and marked with its own language — `lang` on each name, so a
 * screen reader says "Español" in a Spanish voice and the browser sets it by
 * Spanish rules — the one in use chosen, and a choice kept in this browser.
 *
 * Written once for the two controls that offer it, which take the same props:
 * the language control on a way in whose link names none (a SegmentedControl,
 * login/entry-frame.tsx) and Settings (a Select). It comes before a match, so
 * the ways in may read it.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LANGUAGES, type Language } from '../../i18n/config';

/** What a control offering the language takes: its name, the language in use, each language, and the change. */
interface LanguageChoice {
  label: string;
  value: Language;
  options: { value: Language; label: ReactNode }[];
  onChange: (next: Language) => void;
}

/** The language in use: one the client ships. */
export function useLanguage(): Language {
  const { i18n } = useTranslation();
  return (i18n.resolvedLanguage ?? 'en') as Language;
}

/** The player's language, as a SegmentedControl or a Select offers it. */
export function useLanguageChoice(): LanguageChoice {
  const { t, i18n } = useTranslation();
  const value = useLanguage();
  return {
    label: t('settings.language'),
    value,
    options: LANGUAGES.map(({ code, nameKey }) => ({ value: code, label: <span lang={code}>{t(nameKey)}</span> })),
    onChange: (next) => void i18n.changeLanguage(next),
  };
}
